import {
  CLEAR_GONTIJO_SESSION,
  SET_GONTIJO_SESSION,
  SET_GONTIJO_SESSION_HYDRATED,
  SET_GONTIJO_SESSION_VALIDATING,
} from 'constants/ActionTypes';
import {
  selectGontijoSession,
  selectGontijoSessionScope,
  selectLoginOnCheckoutEnabled,
} from 'selectors/gontijoSessionSelectors';
import {
  buildGontijoSessionRecord,
  clearGontijoSession as clearStoredGontijoSession,
  forgetGontijoSessionLocally,
  gontijoSessionStorageKey,
  readGontijoSession,
  readPersistedGontijoSession,
  writeGontijoSession,
} from '@/services/gontijo-session/gontijoSessionStorage';
import {
  NO_SESSION,
  readGontijoSessionVerdictWithDeadline,
  resetGontijoSessionVerdict,
} from '@/services/gontijo-session/gontijoSessionVerdict';

/**
 * Puts a session in the store.
 * @param {Object} record - The session record.
 * @returns {Object} Redux action.
 */
export function setGontijoSession(record) {
  return { type: SET_GONTIJO_SESSION, record };
}

/**
 * Empties the session in the store.
 * @returns {Object} Redux action.
 */
export function clearGontijoSessionState() {
  return { type: CLEAR_GONTIJO_SESSION };
}

/**
 * Marks storage as read, so gates can tell "no session" from "not looked yet".
 * @returns {Object} Redux action.
 */
export function markGontijoSessionHydrated() {
  return { type: SET_GONTIJO_SESSION_HYDRATED };
}

/**
 * Flags a validation round trip.
 * @param {boolean} isValidating - Whether a verdict is being awaited.
 * @returns {Object} Redux action.
 */
export function setGontijoSessionValidating(isValidating) {
  return { type: SET_GONTIJO_SESSION_VALIDATING, isValidating };
}

/**
 * Reads storage into the store. Run once, when the provider mounts.
 *
 * With the flag off this only marks the session hydrated: nothing is read, so a brand that does not
 * use checkout login never touches Gontijo storage at all.
 * @returns {Function} Thunk.
 */
export function hydrateGontijoSession() {
  return (dispatch, getState) => {
    const state = getState();

    if (!selectLoginOnCheckoutEnabled(state)) {
      dispatch(markGontijoSessionHydrated());
      return null;
    }

    const stored = readGontijoSession(selectGontijoSessionScope(state));

    dispatch(stored ? setGontijoSession(stored) : clearGontijoSessionState());
    return stored;
  };
}

/**
 * Records the session the two-factor step just minted.
 *
 * `expires` arrives exactly as the ERP sent it; parsing belongs to the store module, so there is one
 * place that knows the provider's date format rather than one per caller.
 * @param {string} token - The `client_auth`.
 * @param {*} expires - The provider's raw `expires` value.
 * @param {string} [email] - The address the buyer signed in with, when the widget announced one.
 * @returns {Function} Thunk resolving to the stored record.
 */
export function establishGontijoSession(token, expires, email) {
  return (dispatch, getState) => {
    const signedInWith = typeof email === 'string' ? email.trim() : '';
    const record = {
      ...buildGontijoSessionRecord(token, expires),
      ...(signedInWith && { email: signedInWith }),
    };

    // A new session invalidates any verdict still in flight about the previous token.
    resetGontijoSessionVerdict();
    writeGontijoSession(selectGontijoSessionScope(getState()), record);
    dispatch(setGontijoSession(record));

    return record;
  };
}

/**
 * Removes the session everywhere it exists.
 *
 * Storage, the store, and the shared verdict. Leaving any one of them holding the token is a
 * sign-out that does not sign out — and the verdict in particular could otherwise be handed to a
 * reader arriving just after somebody else signs in.
 * @returns {Function} Thunk.
 */
export function signOutGontijoSession() {
  return (dispatch, getState) => {
    resetGontijoSessionVerdict();
    clearStoredGontijoSession(selectGontijoSessionScope(getState()));
    dispatch(clearGontijoSessionState());
  };
}

/**
 * Follows a sign-in or sign-out that happened in another tab.
 *
 * `localStorage` is shared across the brand's tabs, but the store is not: without this, signing out
 * in one tab left every other open tab holding the token in memory and still able to pay with it.
 * The browser's `storage` event only reaches the *other* tabs, so this never answers the tab's own
 * writes.
 *
 * A removal is checked against what storage holds *now*, read directly: the event can arrive after
 * a third tab has already signed in again, and signing out on it would erase that new session. So a
 * record found there is adopted; otherwise this tab drops its own copy — memory fallback and verdict
 * included — without touching the shared key, which is already gone. `readGontijoSession` is not
 * used for that read because its memory fallback would hand the old token straight back.
 * `key === null` is the other tab clearing all of storage.
 * @param {{key: ?string, newValue: ?string}} event - The `storage` event.
 * @returns {Function} Thunk.
 */
export function syncGontijoSessionFromOtherTab({ key, newValue }) {
  return (dispatch, getState) => {
    const state = getState();
    if (!selectLoginOnCheckoutEnabled(state)) return;

    const scope = selectGontijoSessionScope(state);
    if (key !== null && key !== gontijoSessionStorageKey(scope)) return;

    const stored =
      newValue === null ? readPersistedGontijoSession(scope) : readGontijoSession(scope);

    if (!stored) {
      if (newValue !== null) return;

      resetGontijoSessionVerdict();
      forgetGontijoSessionLocally(scope);
      dispatch(clearGontijoSessionState());
      return;
    }

    // Whatever was being asked about the previous token no longer answers for this one.
    resetGontijoSessionVerdict();
    dispatch(setGontijoSession(stored));
  };
}

/**
 * Asks the backend whether the stored session is still good.
 *
 * Only a rejection signs the buyer out. A backend that could not answer — 5xx, no response, or a
 * request that never settled — leaves the session exactly as it was: the carrier having a bad minute
 * is not the buyer's session ending, and treating it as one empties the funnel of signed-in buyers
 * every time the ERP wobbles.
 *
 * The verdict is also checked against the session that asked for it. `resetGontijoSessionVerdict()`
 * drops the shared request, but it cannot cancel one already awaited here: if somebody signs in
 * while an older token is being validated, that older token's rejection would otherwise sign the new
 * buyer straight back out. A verdict about a session that is no longer the session answers nobody.
 * @returns {Function} Thunk resolving to the verdict.
 */
export function revalidateGontijoSession() {
  return async (dispatch, getState) => {
    const { token } = selectGontijoSession(getState());
    if (!token) return NO_SESSION;

    dispatch(setGontijoSessionValidating(true));

    const verdict = await readGontijoSessionVerdictWithDeadline(token);

    // Nothing to dispatch for a session that has since been replaced: whatever replaced it already
    // put the store in the shape it wants, including clearing `isValidating`.
    if (selectGontijoSession(getState()).token !== token) return verdict;

    if (verdict.expired) dispatch(signOutGontijoSession());
    else dispatch(setGontijoSessionValidating(false));

    return verdict;
  };
}
